import { Mention, type MentionOptions } from '@tiptap/extension-mention';
import { CharacterCount } from '@tiptap/extensions/character-count';
import { Placeholder } from '@tiptap/extensions/placeholder';
import { type Editor, Extension, type Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type {
  MentionAttrs,
  TMentionUser,
} from '@voly/ui-components/src/v2/VRichText/VRichTextMention';

export type TMentionSuggestion = MentionOptions<TMentionUser, MentionAttrs>['suggestion'];

interface PointFieldExtensionOptions {
  placeholder?: string;
  showPlaceholderWhenReadOnly?: boolean;
  characterLimit?: number;
  mentionSuggestion?: TMentionSuggestion;
  onEnter?: (editor: Editor) => boolean;
}

type TSubmitOnEnterOptions = {
  onEnter: (editor: Editor) => boolean;
};

const SubmitOnEnter = Extension.create<TSubmitOnEnterOptions>({
  name: 'submitOnEnter',
  addOptions() {
    return { onEnter: () => false };
  },
  addKeyboardShortcuts() {
    return { Enter: () => this.options.onEnter(this.editor) };
  },
});

const mentionExtension = (suggestion?: TMentionSuggestion): Extensions[number] =>
  Mention.configure({
    HTMLAttributes: { class: 'mention' },
    renderText: ({ options, node }) =>
      `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`,
    ...(suggestion ? { suggestion } : {}),
  });

const placeholderExtension = (
  placeholder: string,
  showPlaceholderWhenReadOnly: boolean,
): Extensions[number] =>
  Placeholder.configure({ placeholder, showOnlyWhenEditable: !showPlaceholderWhenReadOnly });

const characterLimitExtension = (limit: number): Extensions[number] =>
  CharacterCount.configure({ limit, autoTrim: false });

export const createPointFieldExtensions = ({
  placeholder,
  showPlaceholderWhenReadOnly = false,
  characterLimit,
  mentionSuggestion,
  onEnter,
}: PointFieldExtensionOptions = {}): Extensions => [
  StarterKit.configure({ link: { openOnClick: false, autolink: false } }),
  mentionExtension(mentionSuggestion),
  ...(placeholder ? [placeholderExtension(placeholder, showPlaceholderWhenReadOnly)] : []),
  ...(characterLimit ? [characterLimitExtension(characterLimit)] : []),
  ...(onEnter ? [SubmitOnEnter.configure({ onEnter })] : []),
];
